<template>
  <div class="section">
    <div class="container">
      <!-- Loading -->
      <div v-if="loading" class="text-center" style="padding: 3rem 0;">
        <div class="loading-spinner" style="margin: 0 auto;"></div>
        <p class="text-muted mt-4">กำลังโหลดข้อมูลสินค้า...</p>
      </div>

      <!-- Not Found -->
      <div v-else-if="!product" class="empty-state">
        <svg class="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <h3 class="empty-title">ไม่พบสินค้า</h3>
        <p class="empty-desc">สินค้าที่คุณค้นหาไม่มีอยู่ในระบบ</p>
        <router-link to="/products" class="btn btn-primary">
          กลับไปหน้าสินค้า
        </router-link>
      </div>

      <!-- Product Detail -->
      <div v-else class="product-detail">
        <!-- Product Images -->
        <div class="product-gallery">
          <div class="main-image">
            <img 
              v-if="product.images && product.images.length > 0" 
              :src="getImageUrl(product.images[selectedImageIndex]?.image_path)" 
              :alt="product.name"
            >
            <div v-else class="image-placeholder">
              <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </div>
          </div>
          <div v-if="product.images && product.images.length > 1" class="thumbnail-list">
            <button 
              v-for="(image, index) in product.images" 
              :key="index"
              @click="selectedImageIndex = index"
              class="thumbnail"
              :class="{ active: selectedImageIndex === index }"
            >
              <img :src="getImageUrl(image.image_path)" :alt="`${product.name} ${index + 1}`">
            </button>
          </div>
        </div>

        <!-- Product Info -->
        <div class="product-info-detail">
          <p v-if="product.category" class="product-category-label">{{ product.category.name }}</p>
          <h1 class="product-title">{{ product.name }}</h1>
          <div class="product-price-large">฿{{ Number(product.price).toLocaleString() }}</div>
          
          <!-- Stock -->
          <div class="stock-info">
            <span v-if="currentStock > 0" class="badge badge-success">
              มีสินค้า {{ currentStock }} ชิ้น
            </span>
            <span v-else class="badge badge-danger">
              สินค้าหมด
            </span>
          </div>

          <p class="product-description">{{ product.description }}</p>

          <!-- Variants -->
          <div v-if="product.has_variants && product.variants" class="variants-section">
            <h3 class="variants-title">เลือกตัวเลือก</h3>
            <div class="variants-list">
              <button 
                v-for="variant in product.variants" 
                :key="variant.id"
                @click="selectedVariant = variant"
                class="variant-btn"
                :class="{ active: selectedVariant?.id === variant.id }"
                :disabled="variant.stock_quantity <= 0"
              >
                {{ getVariantName(variant) }}
                <span v-if="variant.stock_quantity <= 0" class="variant-out">(หมด)</span>
              </button>
            </div>
          </div>

          <!-- Quantity & Add to Cart -->
          <div class="add-to-cart-section">
            <div class="quantity-selector">
              <button @click="decreaseQuantity" class="qty-btn">-</button>
              <span class="qty-value">{{ quantity }}</span>
              <button @click="increaseQuantity" class="qty-btn">+</button>
            </div>
            
            <button 
              @click="addToCart"
              class="btn btn-primary btn-lg add-cart-btn"
              :disabled="!canAddToCart"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
              {{ addToCartButtonText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import api from '../api';
import { useNotification } from '../composables/useNotification';
import { getImageUrl } from '../utils/imageUrl';

const route = useRoute();
const { showSuccess, showError } = useNotification();

const product = ref(null);
const loading = ref(true);
const selectedVariant = ref(null);
const selectedImageIndex = ref(0);
const quantity = ref(1);

const fetchProduct = async () => {
  loading.value = true;
  try {
    const response = await api.get(`/products/${route.params.slug}`);
    product.value = response.data.data;
  } catch (error) {
    console.error('Failed to fetch product:', error);
  } finally {
    loading.value = false;
  }
};

onMounted(fetchProduct);

const currentStock = computed(() => {
  if (!product.value) return 0;
  if (product.value.has_variants) {
    return selectedVariant.value ? selectedVariant.value.stock_quantity : 0;
  }
  return product.value.stock_quantity;
});

const canAddToCart = computed(() => {
  if (!product.value) return false;
  if (product.value.has_variants && !selectedVariant.value) return false;
  return currentStock.value > 0;
});

const addToCartButtonText = computed(() => {
  if (!product.value) return 'กำลังโหลด...';
  if (product.value.has_variants && !selectedVariant.value) return 'เลือกตัวเลือก';
  if (currentStock.value === 0) return 'สินค้าหมด';
  return 'เพิ่มลงตะกร้า';
});

const decreaseQuantity = () => {
  if (quantity.value > 1) quantity.value--;
};

const increaseQuantity = () => {
  if (quantity.value < currentStock.value) quantity.value++;
};

const getVariantName = (variant) => {
  const parts = [];
  if (variant.color) parts.push(variant.color);
  if (variant.size) parts.push(variant.size);
  if (variant.custom_option) parts.push(variant.custom_option);
  return parts.length > 0 ? parts.join(' - ') : `ตัวเลือก #${variant.id}`;
};

const addToCart = async () => {
  try {
    await api.post('/cart', {
      product_id: product.value.id,
      variant_id: selectedVariant.value ? selectedVariant.value.id : null,
      quantity: quantity.value
    });
    showSuccess('เพิ่มลงตะกร้าเรียบร้อยแล้ว!');
    quantity.value = 1;
  } catch (error) {
    showError(error.response?.data?.message || 'เพิ่มลงตะกร้าล้มเหลว');
  }
};
</script>

<style scoped>
.product-detail {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
}

@media (min-width: 768px) {
  .product-detail {
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
  }
}

.product-gallery {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.main-image {
  aspect-ratio: 1;
  background: #f9fafb;
  border-radius: 0.75rem;
  overflow: hidden;
}

.main-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #d1d5db;
}

.thumbnail-list {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
}

.thumbnail {
  width: 64px;
  height: 64px;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 2px solid transparent;
  cursor: pointer;
  flex-shrink: 0;
}

.thumbnail.active {
  border-color: var(--color-primary);
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-category-label {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.product-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 1rem;
}

.product-price-large {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 1rem;
}

.stock-info {
  margin-bottom: 1.5rem;
}

.product-description {
  color: var(--color-text-light);
  line-height: 1.7;
  margin-bottom: 2rem;
}

.variants-section {
  margin-bottom: 2rem;
  padding: 1.5rem 0;
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
}

.variants-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.75rem;
}

.variants-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.variant-btn {
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  background: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.variant-btn:hover:not(:disabled) {
  border-color: var(--color-primary);
}

.variant-btn.active {
  border-color: var(--color-primary);
  background: rgba(217, 119, 6, 0.1);
  color: var(--color-primary);
}

.variant-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.variant-out {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.add-to-cart-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

@media (min-width: 480px) {
  .add-to-cart-section {
    flex-direction: row;
    align-items: center;
  }
}

.quantity-selector {
  display: flex;
  align-items: center;
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  overflow: hidden;
}

.qty-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: var(--color-text);
}

.qty-btn:hover {
  background: #f3f4f6;
}

.qty-value {
  width: 48px;
  text-align: center;
  font-weight: 500;
  border-left: 1px solid var(--color-border);
  border-right: 1px solid var(--color-border);
}

.add-cart-btn {
  flex: 1;
}

.empty-state {
  text-align: center;
  padding: 4rem 1rem;
}

.empty-icon {
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1rem;
  color: var(--color-text-muted);
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.empty-desc {
  color: var(--color-text-light);
  margin-bottom: 1.5rem;
}
</style>
